/*
 * Maelora storefront helpers.
 * Only the announcement bar has a countdown (merchant-set end date, or the preview-only daily demo).
 */

import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const EASTERN = 'America/New_York';

/** Milliseconds that America/New_York is ahead of UTC at the given instant (negative in the US). */
function easternOffset(timestamp) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(timestamp));
  const get = (type) => Number(parts.find((part) => part.type === type)?.value);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return asUtc - Math.floor(timestamp / 1000) * 1000;
}

/** Converts a US Eastern wall-clock time to a UTC timestamp, DST aware. */
export function easternToTimestamp(year, month, day, hour = 0, minute = 0) {
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  let timestamp = wall - easternOffset(wall);
  const corrected = wall - easternOffset(timestamp);
  if (corrected !== timestamp) timestamp = corrected;
  return timestamp;
}

/**
 * Parses a merchant-entered ISO date. With an explicit offset or Z it is used as is,
 * without one it is read as US Eastern time. Returns NaN when the value is invalid.
 */
export function parseMerchantDate(value) {
  const text = String(value || '').trim();
  if (!text) return NaN;
  if (/(z|[+-]\d{2}:?\d{2})$/i.test(text)) return Date.parse(text);
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{1,2}):(\d{2}))?/);
  if (!match) return NaN;
  const [, year, month, day, hour = '0', minute = '0'] = match;
  return easternToTimestamp(+year, +month, +day, +hour, +minute);
}

const pad = (number) => String(number).padStart(2, '0');

/**
 * <maelora-countdown data-end="2026-11-27T00:00" data-hide="[data-countdown-wrap]">
 * Counts down to data-end. Hides itself, or its closest data-hide ancestor, when the end is
 * invalid or has passed.
 */
class MaeloraCountdown extends HTMLElement {
  connectedCallback() {
    this.end = parseMerchantDate(this.dataset.end);
    // Demo only: in the editor or an unpublished-theme preview, count to the next midnight
    // US Eastern and restart daily. Never on the live (main) theme.
    const shopify = /** @type {any} */ (window).Shopify;
    this.demoDaily =
      this.hasAttribute('data-demo-daily') &&
      (Boolean(shopify?.designMode) || (shopify?.theme?.role && shopify.theme.role !== 'main'));
    this.target = (this.dataset.hide && this.closest(this.dataset.hide)) || this;
    this.tick();
    if (!this.target.hidden) this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  nextEasternMidnight() {
    const now = easternNow();
    const today = Date.UTC(now.year, now.month - 1, now.day);
    const tomorrow = new Date(today + 86400000);
    return easternToTimestamp(tomorrow.getUTCFullYear(), tomorrow.getUTCMonth() + 1, tomorrow.getUTCDate(), 0, 0);
  }

  tick() {
    if (this.demoDaily) this.end = this.nextEasternMidnight();
    const left = Math.floor((this.end - Date.now()) / 1000);
    if (!Number.isFinite(left) || left <= 0) {
      this.target.hidden = true;
      clearInterval(this.timer);
      return;
    }
    this.target.hidden = false;
    if (this.dataset.format === 'compact') {
      const days = Math.floor(left / 86400);
      const hours = Math.floor((left % 86400) / 3600);
      const minutes = Math.floor((left % 3600) / 60);
      this.textContent = days > 0 ? `${days}d ${pad(hours)}h ${pad(minutes)}m` : `${pad(hours)}h ${pad(minutes)}m ${pad(left % 60)}s`;
      return;
    }
    const values = {
      d: Math.floor(left / 86400),
      h: Math.floor((left % 86400) / 3600),
      m: Math.floor((left % 3600) / 60),
      s: left % 60,
    };
    const dayUnit = this.querySelector('[data-unit="d"]');
    if (dayUnit instanceof HTMLElement) dayUnit.hidden = this.demoDaily && values.d === 0;
    for (const unit of /** @type {const} */ (['d', 'h', 'm', 's'])) {
      const element = this.querySelector(`[data-unit="${unit}"]`);
      if (element) element.textContent = `${pad(values[unit])}${unit}`;
    }
  }
}

if (!customElements.get('maelora-countdown')) customElements.define('maelora-countdown', MaeloraCountdown);

/**
 * <maelora-expiry data-end="2026-12-18T23:59" data-hide="[data-expiry-wrap]">
 * Shows its target until the merchant-set moment, then hides it.
 */
class MaeloraExpiry extends HTMLElement {
  connectedCallback() {
    const end = parseMerchantDate(this.dataset.end);
    const target = (this.dataset.hide && this.closest(this.dataset.hide)) || this;
    target.hidden = !(Number.isFinite(end) && end > Date.now());
  }
}

if (!customElements.get('maelora-expiry')) customElements.define('maelora-expiry', MaeloraExpiry);

/** Current US Eastern wall clock: weekday 0-6 (Sunday 0), hour, minute, second. */
function easternNow() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN,
    hourCycle: 'h23',
    weekday: 'short',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date());
  const get = (type) => parts.find((part) => part.type === type)?.value || '';
  return {
    weekday: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday')),
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')),
    minute: Number(get('minute')),
    second: Number(get('second')),
  };
}

/* ---------- Cart helpers ---------- */

/** @returns {Promise<any>} the AJAX cart */
export async function getCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
  return response.json();
}

/**
 * Adds several lines in one request and tells Horizon's cart drawer, cart icon and cart items about it,
 * the same way the product form does for a batch add.
 * @param {{ id: string | number, quantity: number }[]} lines
 * @param {HTMLElement} source - element the cart event is dispatched from
 */
export async function addLines(lines, source) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
    .filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: lines.map((line) => ({ id: Number(line.id), quantity: line.quantity })),
        sections: sectionIds.join(','),
      }),
    });
    const data = await response.json();
    const didError = Boolean(data.status);
    const cart = await getCart();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'maelora',
        sourceId: source.id || 'maelora',
        itemCount: lines.reduce((sum, line) => sum + line.quantity, 0),
        sections: didError ? undefined : data.sections,
        didError,
      },
    });

    if (didError) {
      source.dispatchEvent(
        new CartErrorEvent({
          error: data.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: data.description, errors: data.errors },
        })
      );
      throw new Error(data.description || data.message || 'Add to cart failed');
    }

    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/** Runs the callback with a fresh cart after every cart change on the page. */
export function onCartChange(callback) {
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    Promise.resolve(event.promise)
      .catch(() => undefined)
      .then(() => getCart())
      .then(callback)
      .catch(() => undefined);
  });
}

/* ---------- Product page ---------- */

/**
 * <maelora-b2g1 data-mode="product|drawer" data-exclude="productId">
 * Buy 2, Get 1 Free progress in 3 segments. Count = cart pieces (gift box excluded) modulo 3.
 * Display only: Shopify's automatic discount does the pricing.
 */
class MaeloraB2G1 extends HTMLElement {
  connectedCallback() {
    this.exclude = new Set((this.dataset.exclude || '').split(',').filter(Boolean));
    this.mode = this.dataset.mode || 'product';
    getCart().then((cart) => this.render(cart)).catch(() => undefined);
    if (!this.listening) {
      this.listening = true;
      onCartChange((cart) => this.isConnected && this.render(cart));
    }
  }

  render(cart) {
    const pieces = cart.items
      .filter((item) => !this.exclude.has(String(item.variant_id)) && !this.exclude.has(String(item.product_id)))
      .reduce((sum, item) => sum + item.quantity, 0);
    const have = pieces % 3;
    const text = this.querySelector('[data-b2g1-text]');
    const segments = [...this.querySelectorAll('.m-seg i')];
    const plural = (n) => (n === 1 ? 'piece' : 'pieces');

    if (this.mode === 'drawer') {
      this.hidden = pieces === 0;
      const complete = have === 0 && pieces > 0;
      const need = (3 - have) % 3;
      if (text) {
        text.textContent = complete
          ? 'Free piece unlocked: the cheapest of every 3 is free.'
          : `Add ${need} more ${plural(need)} and 1 is free. You now have ${have} of 3.`;
      }
      segments.forEach((segment, i) => {
        segment.className = complete || i < have ? 'is-on' : '';
      });
      this.classList.toggle('is-unlocked', complete);
      return;
    }

    this.hidden = pieces === 0;
    const need = (3 - ((have + 1) % 3)) % 3;
    if (text) {
      text.textContent =
        (need === 0
          ? 'Adding this one makes 3: the cheapest is free.'
          : `Add this + ${need} more ${plural(need)} and 1 is free.`) + ` You now have ${have} of 3.`;
    }
    segments.forEach((segment, i) => {
      segment.className = i < have ? 'is-on' : i === have ? 'is-next' : '';
    });
    this.classList.toggle('is-unlocked', need === 0);
  }
}

/**
 * <maelora-set-add data-variant-ids="1,2">
 * Adds the selected variant of the piece on this page plus the other set pieces in one request.
 */
class MaeloraSetAdd extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.error = this.querySelector('[data-set-error]');
    this.button?.addEventListener('click', this.onClick);
  }

  onClick = () => {
    const product = /** @type {any} */ (document.querySelector('maelora-product'));
    const mainForm = document.querySelector('product-form-component form');
    const currentId =
      product?.variant?.id ?? (mainForm ? new FormData(/** @type {HTMLFormElement} */ (mainForm)).get('id') : null);
    const ids = [currentId, ...(this.dataset.variantIds || '').split(',')].filter(Boolean);
    if (ids.length === 0 || !this.button) return;

    this.button.disabled = true;
    if (this.error) this.error.hidden = true;
    addLines(
      ids.map((id) => ({ id: String(id), quantity: 1 })),
      this
    )
      .catch((reason) => {
        if (this.error) {
          this.error.textContent = reason.message;
          this.error.hidden = false;
        }
      })
      .finally(() => {
        if (this.button) this.button.disabled = false;
      });
  };
}

/** Formats cents like Liquid's money_without_trailing_zeros for USD-style currencies. */
function formatMoney(cents, { trim = true } = {}) {
  const currency = window.Shopify?.currency?.active || 'USD';
  const amount = cents / 100;
  const whole = trim && Number.isInteger(amount);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(amount);
}

/**
 * <maelora-product> - the Maelora product page: options, variant switching, gallery,
 * gift box, add to bag and the sticky bar. Variant data is rendered by Liquid.
 */
class MaeloraProduct extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-product-json]');
    this.variants = json ? JSON.parse(json.textContent || '{}').variants || [] : [];
    this.fieldsets = [...this.querySelectorAll('[data-option-index]')];
    this.addButton = this.querySelector('[data-add]');
    this.gift = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-gift]'));
    this.sticky = this.querySelector('[data-sticky]');
    this.error = this.querySelector('[data-error]');

    const params = new URLSearchParams(location.search);
    const fromUrl = Number(params.get('variant'));
    this.variant =
      this.variants.find((v) => v.id === fromUrl) ||
      this.variants.find((v) => v.options.every((value, i) => this.selectedValue(i) === value)) ||
      this.variants.find((v) => v.available) ||
      this.variants[0];

    this.addEventListener('click', this.onClick);
    this.gift?.addEventListener('change', () => this.render());
    this.setupSticky();
    this.render();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  selectedValue(index) {
    const pressed = this.fieldsets[index]?.querySelector('[aria-pressed="true"]');
    return pressed instanceof HTMLElement ? pressed.dataset.value : undefined;
  }

  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const option = target.closest('[data-value]');
    if (option instanceof HTMLElement && option.closest('[data-option-index]')) {
      const fieldset = option.closest('[data-option-index]');
      fieldset?.querySelectorAll('[data-value]').forEach((button) => button.setAttribute('aria-pressed', String(button === option)));
      const values = this.fieldsets.map((_, i) => this.selectedValue(i));
      this.variant = this.variants.find((v) => v.options.every((value, i) => values[i] === value)) || null;
      if (this.variant) {
        const url = new URL(location.href);
        url.searchParams.set('variant', String(this.variant.id));
        history.replaceState(history.state, '', url);
        if (this.variant.media) this.showMedia(this.variant.media);
      }
      this.render();
      return;
    }

    const qtyChoice = target.closest('[data-qty-choice]');
    if (qtyChoice instanceof HTMLElement) {
      this.setMode = qtyChoice.dataset.qtyChoice === 'set';
      this.querySelectorAll('[data-qty-choice]').forEach((option) => option.setAttribute('aria-checked', String(option === qtyChoice)));
      const builder = this.querySelector('[data-set-builder]');
      if (builder instanceof HTMLElement) builder.hidden = !this.setMode;
      this.render();
      return;
    }

    const pickAdd = target.closest('[data-pick-add]');
    if (pickAdd) {
      this.addPick(pickAdd.closest('[data-pick]'));
      return;
    }

    const slotOpen = target.closest('[data-slot-open]');
    if (slotOpen && !slotOpen.closest('.is-filled')) {
      const picksList = this.querySelector('[data-set-picks]');
      if (picksList instanceof HTMLElement) {
        picksList.hidden = false;
        picksList.scrollIntoView({ behavior: 'smooth', block: 'center' });
        picksList.querySelector('[data-pick-add]:not([disabled])')?.focus({ preventScroll: true });
      }
      return;
    }

    const slotRemove = target.closest('[data-slot-remove]');
    if (slotRemove instanceof HTMLElement) {
      this.picks[Number(slotRemove.dataset.slotRemove) - 1] = null;
      this.renderSet();
      this.render();
      return;
    }

    const thumb = target.closest('[data-thumb]');
    if (thumb instanceof HTMLElement) {
      this.showMedia(Number(thumb.dataset.thumb));
      return;
    }

    if (target.closest('[data-add], [data-sticky-add]')) this.add();
  };

  showMedia(id) {
    this.querySelectorAll('[data-media-id]').forEach((slide) => {
      if (slide instanceof HTMLElement) slide.hidden = Number(slide.dataset.mediaId) !== id;
    });
    this.querySelectorAll('[data-thumb]').forEach((thumb) => {
      if (thumb instanceof HTMLElement) thumb.setAttribute('aria-pressed', String(Number(thumb.dataset.thumb) === id));
    });
  }

  giftPrice() {
    return this.gift?.checked ? Number(this.dataset.giftPrice) || 0 : 0;
  }

  render() {
    const variant = this.variant;
    const available = Boolean(variant?.available);

    // Option labels and availability hints
    this.fieldsets.forEach((fieldset, index) => {
      const label = fieldset.querySelector('[data-option-value]');
      if (label) label.textContent = this.selectedValue(index) || '';
      fieldset.querySelectorAll('[data-value]').forEach((button) => {
        if (!(button instanceof HTMLElement)) return;
        const values = this.fieldsets.map((_, i) => (i === index ? button.dataset.value : this.selectedValue(i)));
        const match = this.variants.find((v) => v.options.every((value, i) => values[i] === value));
        button.toggleAttribute('data-unavailable', !match?.available);
      });
    });

    // Price
    if (variant) {
      const price = this.querySelector('[data-price]');
      if (price) price.textContent = formatMoney(variant.price, { trim: false });
      const compare = this.querySelector('[data-compare]');
      const save = this.querySelector('[data-save]');
      const onSale = variant.compare > variant.price;
      if (compare instanceof HTMLElement) {
        compare.hidden = !onSale;
        compare.textContent = onSale ? formatMoney(variant.compare, { trim: false }) : '';
      }
      if (save instanceof HTMLElement) {
        const diff = variant.compare - variant.price;
        const label = this.dataset.discountDisplay === 'amount'
          ? `Save ${formatMoney(diff, { trim: false })}`
          : `Save ${Math.floor((diff * 100) / variant.compare)}%`;
        save.innerHTML = onSale ? `<span class="m-save">${label}</span>` : '';
      }
      const unitPrice = this.querySelector('[data-unit-price]');
      if (unitPrice) unitPrice.textContent = formatMoney(variant.price, { trim: false });
      const buy3 = this.querySelector('[data-buy3]');
      if (buy3) buy3.textContent = formatMoney(Math.round((variant.price * 2) / 3), { trim: false });
      const stickyPrice = this.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
    }

    // Stock line from real tracked inventory only
    const stock = this.querySelector('[data-stock]');
    if (stock) {
      const template = /** @type {HTMLTemplateElement | null} */ (
        this.querySelector(variant?.low ? '[data-stock-low]' : '[data-stock-ok]')
      );
      stock.innerHTML = available && template ? template.innerHTML.replace('[n]', String(variant.low)) : '';
    }

    // Add button
    if (this.addButton instanceof HTMLButtonElement) {
      this.addButton.disabled = !available;
      const picked = (this.picks || []).filter(Boolean).length;
      if (!available) {
        this.addButton.textContent = this.addButton.dataset.soldout || 'Sold out';
      } else if (this.setMode) {
        this.addButton.disabled = picked < 2;
        this.addButton.textContent = picked < 2 ? `Pick ${2 - picked} more to add your set` : 'Add set of 3 to bag';
      } else {
        this.addButton.textContent = `${this.addButton.dataset.label} · ${formatMoney(variant.price + this.giftPrice())}`;
      }
    }
    const stickyButton = this.querySelector('[data-sticky-add]');
    if (stickyButton instanceof HTMLButtonElement) stickyButton.disabled = !available;
    if (this.setMode) this.renderSet();
    this.dispatchEvent(new CustomEvent('maelora:variant'));
  }

  /** Adds a picked piece to the first empty slot of the set. */
  addPick(card) {
    if (!(card instanceof HTMLElement)) return;
    this.picks = this.picks || [null, null];
    const index = this.picks.findIndex((pick) => !pick);
    if (index === -1) return;
    const field = card.querySelector('[data-pick-variant]');
    let id = '';
    let price = 0;
    let option = '';
    if (field instanceof HTMLSelectElement) {
      const selected = field.selectedOptions[0];
      id = field.value;
      price = Number(selected?.dataset.price) || 0;
      option = selected?.textContent?.trim() || '';
    } else if (field instanceof HTMLInputElement) {
      id = field.value;
      price = Number(field.dataset.price) || 0;
    }
    if (!id) return;
    this.picks[index] = { id, price, option, title: card.dataset.title || '', image: card.dataset.image || '' };
    this.renderSet();
    this.render();
  }

  /** Slots, status line and the set summary (display only; Shopify applies the discount). */
  renderSet() {
    this.picks = this.picks || [null, null];
    this.picks.forEach((pick, i) => {
      const slot = this.querySelector(`[data-slot="${i + 1}"]`);
      if (!slot) return;
      const img = slot.querySelector('.m-slot__img');
      const label = slot.querySelector('.m-slot__label');
      const remove = slot.querySelector('[data-slot-remove]');
      slot.classList.toggle('is-filled', Boolean(pick));
      if (img) img.innerHTML = pick && pick.image ? `<img class="m-cover" src="${pick.image}" alt="">` : '<span class="m-slot__plus" aria-hidden="true">+</span>';
      if (label) label.textContent = pick ? pick.title : 'Pick one';
      if (remove instanceof HTMLElement) remove.hidden = !pick;
    });

    const filled = this.picks.filter(Boolean);
    const need = 2 - filled.length;
    const pickedIds = new Set(filled.map((pick) => String(pick.id)));
    this.querySelectorAll('[data-pick]').forEach((card) => {
      const field = card.querySelector('[data-pick-variant]');
      const id = field instanceof HTMLSelectElement || field instanceof HTMLInputElement ? field.value : '';
      const button = card.querySelector('[data-pick-add]');
      const inSet = pickedIds.has(String(id));
      card.classList.toggle('is-picked', inSet);
      if (button instanceof HTMLButtonElement) {
        button.disabled = inSet;
        button.textContent = inSet ? 'In your set' : 'Add to set';
      }
    });
    const picksList = this.querySelector('.m-set-builder__picks');
    if (picksList instanceof HTMLElement) picksList.hidden = need <= 0;
    const status = this.querySelector('[data-set-status]');
    if (status) {
      status.innerHTML = need > 0
        ? `Pick ${need} more ${need === 1 ? 'piece' : 'pieces'}. The cheapest one is <strong>free</strong>.`
        : 'Your set is complete. The cheapest piece is <strong>free</strong> at checkout.';
    }

    const sum = this.querySelector('[data-set-sum]');
    if (sum instanceof HTMLElement) {
      const prices = [this.variant?.price || 0, ...filled.map((pick) => pick.price)];
      const complete = need <= 0;
      sum.hidden = !complete;
      if (complete) {
        const total = prices.reduce((a, b) => a + b, 0);
        const free = Math.min(...prices);
        const was = this.querySelector('[data-set-was]');
        const totalEl = this.querySelector('[data-set-total]');
        const freeEl = this.querySelector('[data-set-free]');
        if (was) was.textContent = formatMoney(total, { trim: false });
        if (totalEl) totalEl.textContent = formatMoney(total - free, { trim: false });
        if (freeEl) freeEl.textContent = `You save ${formatMoney(free, { trim: false })}`;
      }
    }
  }

  async add() {
    if (!this.variant?.available || !(this.addButton instanceof HTMLButtonElement)) return;
    const lines = [{ id: this.variant.id, quantity: 1 }];
    if (this.setMode) {
      const picks = (this.picks || []).filter(Boolean);
      if (picks.length < 2) return;
      picks.forEach((pick) => lines.push({ id: pick.id, quantity: 1 }));
    }
    if (this.gift?.checked && this.dataset.giftVariant) lines.push({ id: this.dataset.giftVariant, quantity: 1 });

    if (this.error) this.error.hidden = true;
    this.addButton.setAttribute('aria-busy', 'true');
    try {
      await addLines(lines, this);
      if (this.setMode) {
        this.picks = [null, null];
        this.renderSet();
        this.render();
      }
    } catch (reason) {
      if (this.error) {
        this.error.textContent = reason instanceof Error ? reason.message : String(reason);
        this.error.hidden = false;
      }
    } finally {
      this.addButton.removeAttribute('aria-busy');
    }
  }

  setupSticky() {
    if (!this.sticky || !this.addButton) return;
    this.observer = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.sticky?.classList.toggle('is-on', show);
      this.sticky?.setAttribute('aria-hidden', String(!show));
      this.sticky?.querySelector('[data-sticky-add]')?.setAttribute('tabindex', show ? '0' : '-1');
    });
    this.observer.observe(this.addButton);
  }
}

const define = (name, element) => {
  if (!customElements.get(name)) customElements.define(name, element);
};
define('maelora-b2g1', MaeloraB2G1);
define('maelora-set-add', MaeloraSetAdd);
define('maelora-product', MaeloraProduct);

/**
 * <maelora-recommendations data-url="/recommendations/products?...">
 * Loads Shopify's product recommendations into the section.
 */
class MaeloraRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url) return;
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const fresh = html.querySelector('maelora-recommendations');
        if (fresh && fresh.querySelector('.m-card')) {
          this.innerHTML = fresh.innerHTML;
          this.hidden = false;
        }
      })
      .catch(() => undefined);
  }
}

define('maelora-recommendations', MaeloraRecommendations);

/**
 * <maelora-preview-only><template>…</template></maelora-preview-only>
 * Shows example content only in the theme editor or the preview of an unpublished theme, so layouts can be
 * judged before real reviews and photos exist. On the live (main) theme it never renders.
 */
class MaeloraPreviewOnly extends HTMLElement {
  connectedCallback() {
    const shopify = /** @type {any} */ (window).Shopify;
    const isPreview = Boolean(shopify?.designMode) || (shopify?.theme?.role && shopify.theme.role !== 'main');
    const template = this.querySelector('template');
    if (!isPreview || !template) return;
    this.append(template.content.cloneNode(true));
    this.closest('[data-preview-host]')?.removeAttribute('hidden');
  }
}

/**
 * <button is="maelora-quick-add"> is not supported in Safari, so: <maelora-quick-add data-variant-id>
 * wraps a button that adds one unit of a variant.
 */
class MaeloraQuickAdd extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.onClick);
  }

  onClick = () => {
    if (!this.button || !this.dataset.variantId) return;
    const label = this.button.textContent;
    this.button.disabled = true;
    addLines([{ id: this.dataset.variantId, quantity: 1 }], this)
      .then(() => {
        if (this.button) this.button.textContent = this.dataset.added || 'Added';
      })
      .catch(() => {
        if (this.button) this.button.textContent = this.dataset.failed || 'Try again';
      })
      .finally(() => {
        setTimeout(() => {
          if (!this.button) return;
          this.button.disabled = false;
          this.button.textContent = label;
        }, 2000);
      });
  };
}

const RECENT_KEY = 'maelora:recent';

/**
 * <maelora-recently-viewed data-current="handle" data-limit="4" data-section="section-id">
 * Remembers viewed products in this browser and renders their real product cards.
 */
class MaeloraRecentlyViewed extends HTMLElement {
  connectedCallback() {
    const current = this.dataset.current || '';
    const limit = Number(this.dataset.limit) || 4;
    let handles = [];
    try {
      handles = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch {
      handles = [];
    }
    const others = handles.filter((handle) => handle !== current).slice(0, limit);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify([current, ...others].filter(Boolean).slice(0, 12)));
    } catch {
      /* storage unavailable: nothing to remember */
    }
    if (others.length === 0 || !this.dataset.section) return;

    const grid = this.querySelector('[data-recent-grid]');
    Promise.all(
      others.map((handle) =>
        fetch(`${(window.Shopify?.routes?.root || '/').replace(/\/$/, '')}/products/${encodeURIComponent(handle)}?section_id=${this.dataset.section}`)
          .then((response) => (response.ok ? response.text() : ''))
          .then((text) => new DOMParser().parseFromString(text, 'text/html').querySelector('.m-card')?.outerHTML || '')
          .catch(() => '')
      )
    ).then((cards) => {
      const html = cards.filter(Boolean).join('');
      if (!html || !grid) return;
      grid.innerHTML = html;
      this.hidden = false;
    });
  }
}

/**
 * <maelora-card data-variant-id> - product card with the two-part quick-add pill.
 * Single-variant products are added straight away; products with options open the
 * card's variant picker first, so nothing is added blindly.
 */
class MaeloraCard extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-qadd]');
    this.picker = this.querySelector('[data-qpick]');
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onOutside, true);
  }

  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-qadd]')) {
      if (this.picker) this.togglePicker(this.picker.hidden);
      else this.add(this.dataset.variantId);
      return;
    }
    if (target.closest('[data-qpick-close]')) {
      this.togglePicker(false);
      this.button?.focus();
      return;
    }
    const choice = target.closest('[data-qpick-variant]');
    if (choice instanceof HTMLElement) this.add(choice.dataset.qpickVariant);
  };

  onKeydown = (event) => {
    if (event.key === 'Escape' && this.picker && !this.picker.hidden) {
      this.togglePicker(false);
      this.button?.focus();
    }
  };

  onOutside = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) this.togglePicker(false);
  };

  togglePicker(open) {
    if (!this.picker) return;
    this.picker.hidden = !open;
    this.button?.setAttribute('aria-expanded', String(open));
    if (open) {
      document.addEventListener('click', this.onOutside, true);
      this.picker.querySelector('button:not([disabled]):not([data-qpick-close])')?.focus();
    } else {
      document.removeEventListener('click', this.onOutside, true);
    }
  }

  async add(variantId) {
    if (!variantId || !(this.button instanceof HTMLButtonElement)) return;
    const label = this.button.querySelector('[data-qadd-label]');
    const original = label?.textContent;
    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');
    try {
      await addLines([{ id: variantId, quantity: 1 }], this);
      if (label) label.textContent = 'Added';
      this.togglePicker(false);
    } catch {
      if (label) label.textContent = 'Try again';
    } finally {
      this.button.removeAttribute('aria-busy');
      setTimeout(() => {
        if (this.button instanceof HTMLButtonElement) this.button.disabled = false;
        if (label && original) label.textContent = original;
      }, 1800);
    }
  }
}

define('maelora-card', MaeloraCard);
define('maelora-preview-only', MaeloraPreviewOnly);
define('maelora-quick-add', MaeloraQuickAdd);
define('maelora-recently-viewed', MaeloraRecentlyViewed);

/* ---------- Cart drawer confirmation toast ---------- */

/**
 * After an add, shows a short green confirmation at the top of the open cart drawer:
 * "Added to cart", or "Nice! 1 piece is now FREE" when the add completed a set of 3
 * AND Shopify actually applied a line discount. Real cart data only.
 */
function initCartToast() {
  if (window.__maeloraCartToast) return;
  window.__maeloraCartToast = true;

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (event.action !== 'add') return;
    Promise.resolve(event.promise)
      .then(() => new Promise((resolve) => setTimeout(resolve, 250)))
      .then(() => getCart())
      .then((cart) => {
        const dialog = document.querySelector('#cart-drawer dialog[open]');
        if (!dialog) return;
        const giftId = dialog.querySelector('.m-cset')?.getAttribute('data-gift-id') || '';
        const pieces = cart.items
          .filter((item) => String(item.product_id) !== giftId)
          .reduce((sum, item) => sum + item.quantity, 0);
        const discounted = cart.items.some((item) =>
          (item.line_level_discount_allocations || []).some((allocation) => allocation.amount > 0)
        );
        const completedSet = pieces > 0 && pieces % 3 === 0 && discounted;

        dialog.querySelector('.m-toast')?.remove();
        const toast = document.createElement('div');
        toast.className = 'm-toast';
        toast.setAttribute('role', 'status');
        toast.innerHTML = completedSet
          ? 'Nice! 1 piece is now <strong>FREE</strong>'
          : 'Added to cart';
        dialog.append(toast);
        requestAnimationFrame(() => toast.classList.add('is-in'));
        setTimeout(() => {
          toast.classList.remove('is-in');
          setTimeout(() => toast.remove(), 300);
        }, 2000);
      })
      .catch(() => undefined);
  });
}

initCartToast();

/**
 * <maelora-carousel> with [data-carousel-track] and prev/next buttons: scrolls one card per click.
 * Touch devices swipe natively (scroll-snap); the arrows are for desktop.
 */
class MaeloraCarousel extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const direction = target?.closest('[data-carousel-next]') ? 1 : target?.closest('[data-carousel-prev]') ? -1 : 0;
    if (!direction) return;
    const track = this.querySelector('[data-carousel-track]');
    const card = track?.querySelector('li');
    if (!track || !card) return;
    const step = card.getBoundingClientRect().width + 10;
    track.scrollBy({ left: direction * step, behavior: 'smooth' });
  };
}

if (!customElements.get('maelora-carousel')) customElements.define('maelora-carousel', MaeloraCarousel);

/* Cart: an empty "+" slot in the set strip scrolls to the upsell row */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('[data-cset-add]') : null;
  if (!target) return;
  const upsells = target.closest('#cart-drawer')?.querySelector('.m-upsells');
  upsells?.scrollIntoView({ behavior: 'smooth', block: 'start' });
});
